/**
 * 3D 解剖模型库 - 基于 HRA Visible Human GLB 真实解剖模型
 * 模型文件部署于 public/models/anatomy/，随项目打包，无需外部接口
 * 来源: Human Reference Atlas (CC BY 4.0) https://humanatlas.io/3d-reference-library
 */
import {
  assembleModel,
  getModelAssetUrl,
  getModelCdnUrl,
} from '@/utils/anatomyModelLoader'

const MODEL_ICON_SVG = {
  lung: '<svg viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg"><path fill="#e8a598" d="M7 3C4.5 3 3 5.2 3 8c0 2.8 1.2 5.2 3 7.2-.3-2.2-.2-4.5.8-6.5C7.8 6.8 9 4.5 7 3zm10 0c2.5 0 4 2.2 4 5 0 2.8-1.2 5.2-3 7.2.3-2.2.2-4.5-.8-6.5C16.2 6.8 15 4.5 17 3z"/><path fill="none" stroke="#c47862" stroke-width="1.2" d="M12 5v14"/></svg>',
  liver: '<svg viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg"><path fill="#a0522d" d="M4 10c0-3 2.5-6 7-6 2.8 0 5 1.2 6.5 3 1.2 1.4 2 3.2 1.8 5.5-.3 3.5-2.5 7-7.3 7.5C7 20.2 4 17 4 13V10z"/><ellipse fill="#2ecc71" cx="16.5" cy="14" rx="2.2" ry="1.6"/></svg>',
  kidney: '<svg viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg"><path fill="#9b59b6" d="M6 6c-2.5 0-4 2.5-4 5.5 0 4 2 7.5 4 10.5 1.2-2.5 1.5-5 .8-7.5C6.8 11.5 6 8.8 6 6zm12 1c2.5 0 4 2.5 4 5.5 0 4-2 7.5-4 10.5-1.2-2.5-1.5-5-.8-7.5C17.2 12.5 18 9.8 18 7z"/></svg>',
}

function localAsset(filename, cdnName) {
  return {
    url: getModelAssetUrl(filename),
    fallback: getModelCdnUrl(cdnName),
  }
}

const MODEL_CONFIGS = {
  heart: {
    id: 'heart',
    name: '心脏',
    description: 'Visible Human 女性心脏，含四腔室与瓣膜结构',
    targetSize: 2.4,
    assets: [localAsset('heart.glb', 'VH_F_Heart.glb')],
    componentDefs: [
      { id: 'lv', name: '左心室', color: 0x8b1a1a, matchers: ['left_ventricle'] },
      { id: 'rv', name: '右心室', color: 0xc0392b, matchers: ['right_ventricle'] },
      { id: 'la', name: '左心房', color: 0xf5b7b1, matchers: ['left_cardiac_atrium'] },
      { id: 'ra', name: '右心房', color: 0xf0c9c4, matchers: ['right_cardiac_atrium'] },
      { id: 'aorta', name: '主动脉瓣', color: 0xe74c3c, matchers: ['aortic_valve'] },
      { id: 'pa', name: '肺动脉瓣', color: 0xff6b6b, matchers: ['pulmonary_valve'] },
      { id: 'mitral', name: '二尖瓣', color: 0xd63031, matchers: ['mitral_valve'] },
      { id: 'tricuspid', name: '三尖瓣', color: 0xe17055, matchers: ['tricuspid_valve'] },
    ],
    lesionDefs: [
      {
        id: 'lv_hypertrophy',
        name: '左心室肥厚',
        description: '左心室壁异常增厚，影响心脏泵血功能',
        normalDescription: '正常左心室壁厚度约 8-11mm，心肌弹性良好',
        lesionDescription: '肥厚型心肌病：室壁厚度 >15mm，舒张功能受限',
        componentId: 'lv',
        offset: [0.15, 0.05, 0.18],
        cameraDistance: 2.5,
        withRing: true,
      },
    ],
    hotspotDefs: [
      { id: 'hs_lv', label: '左心室', componentId: 'lv', offset: [0.1, 0, 0.2], distance: 2.2 },
      { id: 'hs_aorta', label: '主动脉瓣', componentId: 'aorta', offset: [0, 0.15, 0.15], distance: 2.5 },
      { id: 'hs_lesion', label: '病变区域', componentId: 'lv', offset: [0.18, 0.08, 0.22], distance: 2.0, lesionId: 'lv_hypertrophy' },
    ],
  },

  brain: {
    id: 'brain',
    name: '大脑',
    description: 'Allen Brain 参考图谱，含大脑半球、小脑与脑干',
    targetSize: 2.6,
    assets: [localAsset('brain.glb', 'Allen_F_Brain.glb')],
    hotspotColor: 0xc084fc,
    componentDefs: [
      { id: 'cerebellum', name: '小脑', color: 0xb992cc, matchers: ['cerebellum', 'cerebellar'] },
      { id: 'brainstem', name: '脑干', color: 0x8e6cad, matchers: ['brainstem', 'midbrain', 'pons', 'medulla'] },
      { id: 'cerebrum', name: '大脑皮层', color: 0xd7bde2, matchers: ['brain_hemisphere', 'Allen_brain_hemisphere', 'frontal', 'parietal', 'temporal', 'occipital', 'caudate', 'putamen', 'thalamus', 'hippocampus', 'amygdal'] },
    ],
    lesionDefs: [
      {
        id: 'brain_hemorrhage',
        name: '脑出血',
        description: '脑实质内出血，常由高血压引起',
        normalDescription: '正常脑组织密度均匀，无占位效应',
        lesionDescription: '基底节区高密度影，周围水肿带，中线结构偏移',
        componentId: 'cerebrum',
        offset: [0.12, -0.05, 0.15],
        cameraDistance: 2.8,
      },
    ],
    hotspotDefs: [
      { id: 'hs_cerebrum', label: '大脑皮层', componentId: 'cerebrum', offset: [0, 0.12, 0.18], distance: 3.0 },
      { id: 'hs_cerebellum', label: '小脑', componentId: 'cerebellum', offset: [0, -0.08, 0.2], distance: 2.8 },
      { id: 'hs_lesion', label: '出血灶', componentId: 'cerebrum', offset: [0.15, -0.02, 0.2], distance: 2.2, lesionId: 'brain_hemorrhage' },
    ],
  },

  lung: {
    id: 'lung',
    name: '肺脏',
    description: 'Visible Human 呼吸系统，含双肺与气管支气管树',
    targetSize: 2.8,
    assets: [localAsset('lung.glb', 'VH_F_Lung.glb')],
    hotspotColor: 0xfbbf24,
    componentDefs: [
      { id: 'trachea', name: '气管', color: 0xd4a574, matchers: ['trachea', 'tracheal_cartilage', 'main_bronchus'] },
      { id: 'left_lung', name: '左肺', color: 0xe8a598, matchers: ['left_lungs', 'L_lung', 'lingula'] },
      { id: 'right_lung', name: '右肺', color: 0xdfa08a, matchers: ['lungs_R', 'R_lung'] },
    ],
    lesionDefs: [
      {
        id: 'lung_nodule',
        name: '肺结节',
        description: '右肺上叶可见直径约 8mm 结节影',
        normalDescription: '正常肺野清晰，无结节及渗出影',
        lesionDescription: '右肺上叶实性结节，边界欠清，建议随访或进一步检查',
        componentId: 'right_lung',
        offset: [0.05, 0.2, 0.15],
        cameraDistance: 2.8,
        withRing: true,
      },
    ],
    hotspotDefs: [
      { id: 'hs_left_lung', label: '左肺', componentId: 'left_lung', offset: [-0.05, 0.05, 0.18], distance: 3.0 },
      { id: 'hs_right_lung', label: '右肺', componentId: 'right_lung', offset: [0.05, 0.05, 0.18], distance: 3.0 },
      { id: 'hs_trachea', label: '气管', componentId: 'trachea', offset: [0, 0.15, 0.12], distance: 2.8 },
      { id: 'hs_lesion', label: '肺结节', componentId: 'right_lung', offset: [0.08, 0.18, 0.2], distance: 2.2, lesionId: 'lung_nodule' },
    ],
  },

  liver: {
    id: 'liver',
    name: '肝脏',
    description: 'Visible Human 肝脏，含 Couinaud 分段、肝门与韧带结构',
    targetSize: 2.5,
    colorBlend: 0.28,
    assets: [localAsset('liver.glb', 'VH_F_Liver.glb')],
    hotspotColor: 0xf97316,
    componentDefs: [
      { id: 'capsule', name: '肝包膜', color: 0x6b3e26, matchers: ['capsule_of_the_liver', 'bare_area', 'diaphragmatic_surface'] },
      { id: 'right_lobe', name: '右叶', color: 0xa0522d, matchers: ['right_lobe_of_liver', 'right_anterosuperior', 'right_posteroinferior', 'right_posterosuperior', 'right_anteroinferior'] },
      { id: 'left_lobe', name: '左叶', color: 0x8b6914, matchers: ['left_lobe_of_liver', 'left_anterolateral', 'left_superomedial', 'left_inferomedial', 'liver_left', 'quadrate_lobe'] },
      { id: 'caudate', name: '尾状叶', color: 0x7a3e1a, matchers: ['caudate_lobe'] },
      { id: 'portal_vein', name: '肝门/血管', color: 0x2471a3, matchers: ['porta_hepatis', 'hepataduodenal', 'ligamentum_venosum'] },
      { id: 'ligament', name: '肝韧带', color: 0xb8956a, matchers: ['falciform', 'coronary_ligament', 'triangular_ligament', 'round_ligament', 'ligament_of_liver'] },
      { id: 'impression', name: '脏器压迹', color: 0x5c4033, matchers: ['impression_of_liver', 'gastric_impression', 'renal_impression', 'duodenal_impression', 'esophageal_impression', 'colic_impression', 'suprarenal_impression'] },
    ],
    lesionDefs: [
      {
        id: 'liver_tumor',
        name: '肝占位（HCC）',
        description: '肝右叶可见占位性病变，需与正常肝实质对比',
        normalDescription: '正常肝实质：密度均匀，增强后均匀强化；肝叶比例正常，血管走行自然；无占位效应，肝周间隙清晰。',
        lesionDescription: '病变表现：右叶见 3.2cm 低密度灶，边界欠清；动脉期明显强化，门脉期/延迟期快退；提示肝细胞癌（HCC）可能，伴局部肝包膜轻度隆起。',
        componentId: 'right_lobe',
        offset: [0.12, 0.08, 0.18],
        cameraDistance: 2.6,
        withRing: true,
      },
    ],
    hotspotDefs: [
      { id: 'hs_right_lobe', label: '右叶', componentId: 'right_lobe', offset: [0.1, 0.05, 0.18], distance: 2.6 },
      { id: 'hs_left_lobe', label: '左叶', componentId: 'left_lobe', offset: [-0.08, 0.05, 0.16], distance: 2.6 },
      { id: 'hs_portal', label: '肝门', componentId: 'portal_vein', offset: [0, 0.05, 0.15], distance: 2.4 },
      { id: 'hs_lesion', label: '占位灶', componentId: 'right_lobe', offset: [0.14, 0.1, 0.2], distance: 2.0, lesionId: 'liver_tumor' },
    ],
  },

  kidney: {
    id: 'kidney',
    name: '肾脏',
    description: 'Visible Human 双侧肾脏，含肾盂肾盏结构',
    targetSize: 2.6,
    assets: [
      localAsset('kidney_l.glb', 'VH_F_Kidney_L.glb'),
      localAsset('kidney_r.glb', 'VH_F_Kidney_R.glb'),
    ],
    hotspotColor: 0xa855f7,
    componentDefs: [
      { id: 'left_kidney', name: '左肾', color: 0x9b59b6, matchers: ['left_kidney', 'kidney_capsule_L', 'cortex_of_kidney_L'] },
      { id: 'right_kidney', name: '右肾', color: 0x8e44ad, matchers: ['right_kidney', 'kidney_capsule_R', 'cortex_of_kidney_R'] },
      { id: 'left_pelvis', name: '左肾盂', color: 0xe67e22, matchers: ['hilum_of_kidney_L', 'renal_medulla_L'] },
      { id: 'right_pelvis', name: '右肾盂', color: 0xd35400, matchers: ['hilum_of_kidney_R', 'renal_medulla_R'] },
    ],
    lesionDefs: [
      {
        id: 'kidney_cyst',
        name: '肾囊肿',
        description: '右肾可见边界清晰的囊性低密度影',
        normalDescription: '正常肾实质回声均匀，集合系统无分离',
        lesionDescription: '右肾中部 2.5cm 单纯性囊肿，壁薄光滑，内无回声',
        componentId: 'right_kidney',
        offset: [0.08, 0, 0.15],
        cameraDistance: 2.5,
      },
    ],
    hotspotDefs: [
      { id: 'hs_left_kidney', label: '左肾', componentId: 'left_kidney', offset: [-0.05, 0.05, 0.18], distance: 2.8 },
      { id: 'hs_right_kidney', label: '右肾', componentId: 'right_kidney', offset: [0.05, 0.05, 0.18], distance: 2.8 },
      { id: 'hs_cyst', label: '肾囊肿', componentId: 'right_kidney', offset: [0.1, 0.02, 0.2], distance: 2.2, lesionId: 'kidney_cyst' },
    ],
  },
}

export const MODEL_LIBRARY = [
  { id: 'heart', name: '心脏', icon: '❤️' },
  { id: 'brain', name: '大脑', icon: '🧠' },
  { id: 'lung', name: '肺脏', iconSvg: MODEL_ICON_SVG.lung },
  { id: 'liver', name: '肝脏', iconSvg: MODEL_ICON_SVG.liver },
  { id: 'kidney', name: '肾脏', iconSvg: MODEL_ICON_SVG.kidney },
]

export async function loadModel(modelId) {
  const config = MODEL_CONFIGS[modelId]
  if (!config) return null
  return assembleModel(config)
}

export function buildVisibleMap(model) {
  const vis = {}
  if (model) {
    model.components.forEach((c) => {
      vis[c.id] = true
    })
  }
  return vis
}
